import { plainToClass } from 'class-transformer';
import React, { useEffect, useState } from 'react';
import { useQuery } from 'react-apollo';
import { Definition } from '../../../data/Definition';
import { GET_ALL_CROPS, GET_ALL_CROP_VARIETIES, GET_DATA_DEFINITIONS } from '../../../queries';
import { GetAllCrops, GetAllCropVarieties, GetAllCrops_landscan_crops, GetAllCropVarieties_landscan_varieties, GetDataDefinitions, GetDataDefinitions_syncarto_app_data_definition } from '../../../__generated__/types';
import { LoadingSpinner } from '../components';
import { State, initialState } from '../state/state';
import { Crop, Variety } from "../types/types";
import { SEASON_YEAR_DEFAULT, SETTING_SEASON_YEAR } from '../utils/settings';
import {createSocket} from '../../../utils';
import * as turf from '@turf/turf';

const Store = React.createContext<{
  state: State,
  loading: boolean,
  currentUser: any,
  orgId: string,
  userOrgs: any,
  refreshAllCredentials: (orgId?: string) => Promise<any>
  settings: any,
  updateSettings: (key: string, value: string) => void,
  socket: any,
  updateFieldSelected: (field: any) => void,
  startSocket: () => void,
  updateLayersForExport: (layers:any[] | undefined) => void,
  setDownloadDateRange: (dates:any) => void,
  messageFromWorker: any,
}>({
  state: initialState,
  loading: true,
  currentUser: null,
  orgId: '',
  userOrgs: null,
  refreshAllCredentials: async (orgId?: string) => {},
  settings: {},
  updateSettings: (key: string, value: string) => {},
  socket: null,
  updateFieldSelected: (field: any) => {},
  startSocket: () => {},
  updateLayersForExport: (layers:any[] | undefined) => {},
  setDownloadDateRange: (dates:any) => {},
  messageFromWorker: {},
}); 

export const useStore = () => React.useContext(Store);

type AppProviderProps = {
  children?: React.ReactNode,
  currentUser?: any,
  userOrgs: any,
  refreshAllCredentials:  (orgId?: string) => Promise<any>
};

const AppProvider = ({ children, currentUser, userOrgs, refreshAllCredentials }: AppProviderProps) => {
  const [state, setState] = useState<State>(initialState);
  const [settings, setSettings] = useState<{}>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [socket, setSocket] = useState<any>(undefined);
  const [fieldSelected, setFieldSelected] = useState<any>(undefined);
  const [layersForExport, setLayersForExport] = useState<any[] | undefined>(undefined);
  const [incrementSocket, setIncrementSocket] = useState<number>(0);
  const [bBox, setBBox] = useState<number[] | undefined>(undefined);
  const [downloadDateRange, setDownloadDateRange] = useState<any>(null)
  const [messageFromWorker, setMessageFromWorker] = useState({chat:"Export raster layers for dates selected on timeline.", shipment:"", processing:false});

  const { data: cropData, loading: cropLoading } = useQuery<GetAllCrops>(GET_ALL_CROPS, { fetchPolicy: "no-cache" });
  const { data: varietyData, loading: varietyLoading } = useQuery<GetAllCropVarieties>(GET_ALL_CROP_VARIETIES, { fetchPolicy: "no-cache" });
  const { data: definitionData, loading: definitionLoading } = useQuery<GetDataDefinitions>(GET_DATA_DEFINITIONS, { fetchPolicy: "no-cache" });
  const serverUrl = process.env.REACT_APP_CHATBOT_URL as string;
  
  useEffect(() => {
    // load the default settings
    updateSettings(SETTING_SEASON_YEAR, SEASON_YEAR_DEFAULT.toString());
  }, []);

  useEffect(() => {
    let newState = { ...state };
    newState.crops = !cropData ? [] : cropData.landscan_crops.map((item: GetAllCrops_landscan_crops) => {
      return {
        id: item.crop_id,
        name: item.name,
        description: item.description
      } as Crop;
    });

    setState(newState);
  }, [cropData]);

  useEffect(() => {
    let newState = { ...state };
    newState.varieties = !varietyData ? [] : varietyData.landscan_varieties.map((item: GetAllCropVarieties_landscan_varieties) => {
      return {
        id: item.variety_id,
        crop_id: item.crop_id,
        name: item.name,
        description: item.description
      } as Variety;
    });

    setState(newState);
  }, [varietyData]);

  useEffect(() => {
    let newState = { ...state };
    newState.definitions = !definitionData ? [] : definitionData.syncarto_app_data_definition.map((item: GetDataDefinitions_syncarto_app_data_definition) => {
      const definition = plainToClass(Definition, item.definition);
      definition.id = item.id;
      return definition;
    });
    setState(newState);
  }, [definitionData]);

  useEffect(() => {
    setLoading(cropLoading || varietyLoading || definitionLoading);
  }, [cropLoading, varietyLoading, definitionLoading]);

  const updateSettings = (key: string, value: string) => {
    const obj = {...settings};
    obj[key] = value;
    setSettings(obj);
  }

  // Handle resources needed for data download (clip/zip/ship)
  // 
  function getFormattedTimestamp() {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0'); // Months are 0-based, so add 1
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
    //const milliseconds = String(now.getMilliseconds()).padStart(3, '0');
    return `${year}-${month}-${day}T${hours}${minutes}${seconds}`;
  }
 
  useEffect(() => {
    console.log("083023 [incrementSocket] currentUser", currentUser);
    console.log("083123 [incrementSocket] fieldSelected", fieldSelected);
    if (fieldSelected){
      //let sessionId = `${currentUser.orgId}-${Date.now()}`;
      const timestamp = getFormattedTimestamp();
      let sessionId = `${fieldSelected.id}-${timestamp}`;
      // Advanced Dashboard's sessionId is in the format of:
      //              "d928b2cb-ace4-4870-b6a2-5d67380db26c-Xu59-Kj6";
      //              "<dashboardId>-<sessionId>
      console.log("090523 [incrementSocket] STARTING SOCKET.........", sessionId);
      setSocket(createSocket(serverUrl, sessionId, true));
    }
  }, [incrementSocket]);

  useEffect(() => {
    if (bBox && socket){
      console.log("083123 AppProvider > emitting bbox", bBox);
      socket.emit('map', {
        command: 'setAOI',
        data: bBox
      });
    }
  }, [socket, bBox]);

  useEffect(() => {
    if (socket){
      console.log("090523 AppProvider > initializing socket events");
      socket.on('chat', (msg: any) => {
        console.log("090823 AppProvider > chat", msg);
        let clippedString = msg;
        let processing = false;
        if (msg.includes("Message from image processing worker:")) {
          // Clip out the string by splitting at ":" and taking the second part
          clippedString = msg.split("worker:", 2)[1].trim();
        } else {
          if (msg.includes("Job submitted for processing.")){
            clippedString = msg;
            processing = true;
          }else{
            clippedString = "";
          }
        }
        console.log("090523 AppProvider > messageFromWorker", messageFromWorker); 
        if (clippedString !== ""){
          if (processing){
            setMessageFromWorker(prev => ({
            ...prev, 
            chat: clippedString, 
            processing: processing,
            }));
          }else{
            setMessageFromWorker(prev => ({
            ...prev, 
            chat: clippedString
            }));
          }
        }
      });
      socket.on('shipment', (msg: string) => {
        console.log("090823 AppProvider > shipment", msg); 
        console.log("090523 AppProvider > messageFromWorker", messageFromWorker); 
        let pastZip = ""//messageFromWorker?.shipment ?  messageFromWorker.shipment + "</br>" : "";
        setMessageFromWorker(prev => ({
          ...prev,
          shipment: `${pastZip}<a href='${msg}'>Download .zip</a>.`, 
          processing: false
        }));
      });
    }
  }, [socket]);

  useEffect(() => {
    if (bBox && socket && layersForExport && downloadDateRange){
      console.log("083123 AppProvider > emitting layersForExport", layersForExport);
      let formattedLayersForExport = layersForExport.map((layer:any) => {
          let _layer = {
            dataSourceRow: layer.source,
            title:layer.name,
            yearStart:downloadDateRange.yearStart,
            yearEnd:downloadDateRange.yearEnd,
            visible:layer.is_visible,
        }
        return _layer
      });
      let params = {
        "command":"layerChange",
        "layers":formattedLayersForExport,
      }
      console.log("090523 AppProvider > emitting params", params);
      socket.emit(
        'map', 
        params
      );
    }
  }, [socket, bBox, layersForExport, downloadDateRange]);

  useEffect(() => {
      console.log("083123 AppProvider > downloadDateRange", downloadDateRange);
  }, [downloadDateRange]);

  const updateLayersForExport = (layers: any) => {
    console.log("090523 AppProvider > updateLayersForExport layers", layers)
    setLayersForExport(layers);
  }
  const updateFieldSelected = (field: any) => {
    console.log("083123 AppProvider > updateFieldSelected > field.geom.coordinates", field.geom.coordinates)
    console.log("083123 AppProvider > updateFieldSelected > field", field)
    console.log("083123 AppProvider > updateFieldSelected > fieldSelected", fieldSelected)
    if (field.id !== fieldSelected?.id || !fieldSelected){
      setFieldSelected(field); 
      updateBBox(field.geom.coordinates[0])
      if (socket){
        console.log("083123 AppProvider > updateFieldSelected > setIncrementSocket", incrementSocket)
        setIncrementSocket(prev => prev + 1 );
      }
    }
  }
  const startSocket = () => {
    console.log("083123 AppProvider > startSocket", socket)
    if (!socket){
      setIncrementSocket(prev => prev + 1 );
    }
  }
  const updateBBox = (polygonCoords:any[]) => {
    const polygon = turf.polygon(polygonCoords);
    const bbox = turf.bbox(polygon);
    console.log("083123 AppProvider > updateBBox", bbox)
    setBBox(bbox);
  }

  if(loading) {
    return (
    <div className="d-flex vh-100-1 justify-content-center align-items-center">
      <LoadingSpinner message="Loading..." />
    </div>);
  }
  return (
    <Store.Provider value={{ state, loading, orgId: currentUser.orgId, currentUser, userOrgs, refreshAllCredentials, settings, updateSettings, socket, updateFieldSelected, startSocket, updateLayersForExport, setDownloadDateRange, messageFromWorker }}>{children}</Store.Provider>
  );
};

export default AppProvider;
